import { Subject } from 'rxjs';
import { debounceTime, map, takeUntil } from 'rxjs/operators';

import {
  Input,
  Output,
  OnInit,
  Component,
  OnDestroy,
  EventEmitter,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';

import { SnTag, SnContentType } from '@lib/api/entities';
import { SnTagsAPI } from '@lib/api/services';
import { snFadeInUp } from '@lib/common/animations';
import { SnSelectionModel } from '@lib/common/models';

@Component({
  selector: 'sn-tag-selection',
  templateUrl: './tag-selection.component.html',
  styleUrls: ['./tag-selection.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [snFadeInUp],
})
export class SnTagSelectionComponent implements OnInit, OnDestroy {
  @Input()
  public contentType: SnContentType;

  @Input()
  public selectedTags: SnTag[];

  @Output()
  public readonly tagsSelectionChanged = new EventEmitter<SnTag[]>();

  public selection: SnSelectionModel<SnTag>;

  public isLoading = false;
  public search: string;
  public options: SnTag[];

  private readonly _searchChange$ = new Subject<void>();
  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _tagsAPI: SnTagsAPI,
  ) {}

  public ngOnInit(): void {
    this.selection = new SnSelectionModel(true, [...this.selectedTags], (item) => item.id);
    this._loadOptions();
    this._initSubscriptions();
  }

  public ngOnDestroy(): void {
    this._searchChange$.complete();
    this.tagsSelectionChanged.complete();

    this._destroy$.next();
    this._destroy$.complete();
  }

  public trackTag(index: number, item: SnTag): any {
    return item.id;
  }

  public onSearchChange(): void {
    this._searchChange$.next();
  }

  public selectTag(tag?: SnTag): void {
    const targetTag = tag ? tag : new SnTag({ name: this.search });

    this.selection.toggle(targetTag);
    this.acceptTags();
  }

  public acceptTags(): void {
    this.tagsSelectionChanged.next(this.selection.selected);
  }

  private _initSubscriptions(): void {
    this._searchChange$.pipe(debounceTime(300), takeUntil(this._destroy$)).subscribe(() => {
      this._loadOptions();
    });
  }

  private _loadOptions(): void {
    const params = {
      isOwned: true,
      search: this.search,
    };

    this.isLoading = true;
    this._tagsAPI
      .getTagList(params)
      .pipe(
        map((listResponse) => listResponse.results),
        takeUntil(this._destroy$),
      )
      .subscribe({
        next: (options: SnTag[]) => {
          this.options = options;
          this.isLoading = false;
          this._changeDetectorRef.markForCheck();
        },
        error: () => {
          this.isLoading = false;
          this._changeDetectorRef.markForCheck();
        },
      });
  }
}
